class Filters {
    constructor(map, get_all_features, get_map_loaded, get_site_listing) {
        this.map = map;
        this.get_all_features = get_all_features;
        this.get_map_loaded = get_map_loaded;
        this.get_site_listing = get_site_listing;
        this.selected_zip = '';
        // Two-letter code of the searched location's state. While set, the map
        // and the list show only that state's sites, so visitors are sent only
        // to sites in their own state.
        this.selected_state = '';
        this.debounce_timer = null;
        this.zip_input = document.getElementById('pcsl-zip-input');
        this.suggestions_list = document.getElementById('pcsl-zip-suggestions');

        this._setup_listeners();
        this.update_search_term();
    }

    // Sites matching the type and state filters; shared by the map and the list.
    get_filtered_features() {
        const pin_code = document.getElementById('pcsl-pin-code-select').value;
        const state = this.selected_state;
        if (!pin_code && !state) return this.get_all_features();
        return this.get_all_features().filter(f =>
            (!pin_code || (f.properties.pin_code && f.properties.pin_code.includes(pin_code))) &&
            (!state || f.properties.state === state));
    }

    // Pass reveal: false when the caller is about to move the map itself, so the
    // refit below doesn't fight that move.
    apply({ reveal = true } = {}) {
        if (!this.get_map_loaded() || !this.get_all_features().length) return;

        if (!document.getElementById('pcsl-pin-code-select').value && !this.selected_state) {
            this.map.getSource('pcsl-sites').setData(pcsl_data.geojson_url);
            return;
        }

        this.map.getSource('pcsl-sites').setData({ type: 'FeatureCollection', features: this.get_filtered_features() });
        if (reveal) this.reveal_nearest();
    }

    // If no filtered site is in view, widen the view just enough to take in the
    // nearest one, e.g. a ZIP search in a state with few sites.
    reveal_nearest() {
        const features = this.get_filtered_features();
        const bounds = visible_bounds(this.map);
        const any_visible = features.some(f => bounds.contains(f.geometry.coordinates));
        if (!any_visible && features.length) {
            const center = this.map.getCenter();
            const nearest = features.reduce((closest, f) => {
                const [lng, lat] = f.geometry.coordinates;
                const [clng, clat] = closest.geometry.coordinates;
                return ((lng - center.lng) ** 2 + (lat - center.lat) ** 2) <
                       ((clng - center.lng) ** 2 + (clat - center.lat) ** 2) ? f : closest;
            });
            const [nlng, nlat] = nearest.geometry.coordinates;
            this.map.fitBounds(
                [[Math.min(center.lng, nlng), Math.min(center.lat, nlat)],
                 [Math.max(center.lng, nlng), Math.max(center.lat, nlat)]],
                { padding: 80, maxZoom: this.map.getZoom() }
            );
        }
    }

    set_state(state, label = '') {
        this.selected_state = state;
        this.selected_zip = label;
        this.zip_input.value = label;
        this.update_search_term();
    }

    clear_location() {
        if (!this.selected_zip && !this.selected_state) return;
        this.selected_zip = '';
        this.selected_state = '';
        this.apply({ reveal: false });
        this.update_search_term();
    }

    clear() {
        this.selected_zip = '';
        this.selected_state = '';
        this.zip_input.value = '';
        this.suggestions_list.innerHTML = '';
        this._sync_suggestions_class();
        document.getElementById('pcsl-pin-code-select').value = '';
        this.apply();
        this.update_search_term();
        this._update_type_badge();
    }

    _sync_suggestions_class() {
        this.zip_input.classList.toggle('pcsl-showing-suggestions', this.suggestions_list.children.length > 0);
    }

    _update_type_badge() {
        const container = document.querySelector('.pcsl-filters');
        const existing = container.querySelector('.pcsl-type-active-filter');
        if (existing) existing.remove();

        const pin_select = document.getElementById('pcsl-pin-code-select');
        if (!pin_select.value) return;

        const badge = document.createElement('div');
        badge.className = 'pcsl-type-active-filter';
        badge.innerHTML = '<div class="pcsl-type-active-filter-close">x</div>' + pin_select.options[pin_select.selectedIndex].text;
        badge.addEventListener('click', () => {
            pin_select.value = '';
            this.apply();
            this.update_search_term();
            this._update_type_badge();
        });
        container.appendChild(badge);
    }

    update_search_term() {
        const search_term_el = document.querySelector('.pcsl-search-term');
        const pin_select = document.getElementById('pcsl-pin-code-select');
        const labels = [];

        if (this.selected_zip) {
            labels.push(this.selected_zip);
        }

        if (pin_select.value) {
            labels.push(pin_select.options[pin_select.selectedIndex].text);
        }

        if (labels.length) {
            search_term_el.textContent = labels.join(', ');
            search_term_el.classList.add('active-filters');
        } else {
            search_term_el.textContent = search_term_el.dataset.placeholder;
            search_term_el.classList.remove('active-filters');
        }
    }

    _align_filters_group(filters_inner) {
        const group = filters_inner.querySelector('.pcsl-filters-inner-group');
        if (!group) return;
        const parent = filters_inner.offsetParent;
        const inner_rect = filters_inner.getBoundingClientRect();
        const parent_rect = parent ? parent.getBoundingClientRect() : { left: 0, right: document.documentElement.clientWidth };
        group.style.left = (inner_rect.left - parent_rect.left) + 'px';
        group.style.right = (parent_rect.right - inner_rect.right) + 'px';
        group.style.top = filters_inner.querySelector('.pcsl-mobile-open-trigger').offsetHeight + 2 + 'px';
    }

    _unalign_filters_group(filters_inner) {
        const group = filters_inner.querySelector('.pcsl-filters-inner-group');
        if (!group) return;
        group.style.left = '';
        group.style.right = '';
        group.style.top = '';
    }

    _setup_listeners() {
        let last_width = window.innerWidth;
        window.addEventListener('resize', () => {
            if (window.innerWidth === last_width) return;
            last_width = window.innerWidth;
            const filters_inner = document.querySelector('.pcsl-filters-inner');
            if (filters_inner?.classList.contains('open')) {
                filters_inner.classList.remove('open');
                this._unalign_filters_group(filters_inner);
            }
        });


        document.querySelector('.pcsl-mobile-open-trigger').addEventListener('click', (event) => {
            const filters_inner = event.target.closest('.pcsl-filters-inner');
            filters_inner.classList.toggle('open');
            if (filters_inner.classList.contains('open')) {
                this._align_filters_group(filters_inner);
                document.querySelector('input.pcsl-zip-input').focus();
            } else {
                this._unalign_filters_group(filters_inner);
            }
        });

        document.querySelector('.pcsl-search-term').addEventListener('click', (event) => {
            const el = event.currentTarget;
            if (!el.classList.contains('active-filters')) return;
            if (event.offsetX < el.offsetWidth - 48) return;
            this.clear();
        });

        // The active search now also limits results to its state, so focusing the
        // box no longer drops it; the text is selected so typing replaces it, and
        // emptying the box (below) or the search term's clear button removes it.
        this.zip_input.addEventListener('focus', () => {
            this.zip_input.select();
            this.suggestions_list.innerHTML = '';
            this._sync_suggestions_class();
        });

        this.zip_input.addEventListener('input', () => {
            clearTimeout(this.debounce_timer);
            const query = this.zip_input.value.trim();
            if (!query) {
                this.suggestions_list.innerHTML = '';
                this._sync_suggestions_class();
                this.clear_location();
                return;
            }
            if(query.length < 2) {
                return;
            }

            this.debounce_timer = setTimeout(() => {
                const is_zip = /^\d+$/.test(query);
                //const search_type = is_zip ? 'postcode' : 'place';
                const search_type = 'address,place,postcode,region,neighborhood,locality,district';
                fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?types=${search_type}&country=US&limit=10&access_token=${pcsl_data.mapbox_token}`)
                    .then(r => r.json())
                    .then(data => {
                        this.suggestions_list.innerHTML = '';
                        if (!(data.features || []).length) {
                            const li = document.createElement('li');
                            li.className = 'pcsl-suggestion-no-results';
                            li.textContent = pcsl_data.labels.invalid_location;
                            this.suggestions_list.appendChild(li);
                            this._sync_suggestions_class();
                            return;
                        }
                        const paintcare_states_for_sort = pcsl_data.paintcare_states || [];
                        const is_paintcare_feature = (feature) => {
                            const ctx = feature.context || [];
                            const is_region = feature.place_type?.includes('region');
                            const region = is_region ? feature : ctx.find(c => c.id.startsWith('region.'));
                            const abbr = (is_region ? feature.properties?.short_code : region?.short_code)?.replace('US-', '') ?? '';
                            return !abbr || paintcare_states_for_sort.includes(abbr);
                        };
                        // Stable sort (PaintCare first, Mapbox's relevance preserved within each group), then trim to the top 5.
                        const sorted_features = [...(data.features || [])].sort((a, b) => {
                            return (is_paintcare_feature(a) ? 0 : 1) - (is_paintcare_feature(b) ? 0 : 1);
                        }).slice(0, 5);
                        sorted_features.forEach(feature => {
                            const li = document.createElement('li');
                            const ctx = feature.context || [];
                            const is_region = feature.place_type?.includes('region');
                            const region = is_region ? feature : ctx.find(c => c.id.startsWith('region.'));
                            const state = is_region ? feature.text : (region?.text ?? '');
                            const state_abbr = (is_region ? feature.properties?.short_code : region?.short_code)?.replace('US-', '') ?? '';
                            const country = ctx.find(c => c.id.startsWith('country.'))?.text ?? '';
                            let line1, line2;
                            if (is_zip) {
                                const city = ctx.find(c => c.id.startsWith('place.'))?.text ?? '';
                                line1 = city;
                                line2 = [state, `${feature.text} ${country}`].filter(Boolean).join(', ');
                            } else if (is_region) {
                                line1 = feature.text;
                                line2 = country;
                            } else {
                                line1 = feature.text;
                                line2 = [state, country].filter(Boolean).join(', ');
                            }
                            const paintcare_states = pcsl_data.paintcare_states || [];
                            const is_paintcare_state = !state_abbr || paintcare_states.includes(state_abbr);
                            const non_paintcare_chip = is_paintcare_state ? '' : `<div class="pcsl-site-card-non-paintcare-state pcsl-site-card-chip">${state_abbr} ${pcsl_data.labels.not_paintcare_state_suffix}</div>`;
                            li.innerHTML = `${non_paintcare_chip}<div class="pcsl-suggestion-line-1">${line1}</div><div class="pcsl-suggestion-line-2">${line2}</div>`;
                            li.addEventListener('click', () => {
                                this.selected_zip = feature.text;
                                this.selected_state = state_abbr;
                                this.zip_input.value = feature.place_name;
                                this.suggestions_list.innerHTML = '';
                                this._sync_suggestions_class();
                                this.apply({ reveal: false });
                                if (feature.place_type?.includes('region') && feature.bbox) {
                                    this.map.fitBounds(feature.bbox, { padding: 40 });
                                } else {
                                    this.map.flyTo({ center: feature.center, zoom: auto_zoom });
                                }
                                // Wait for the move to finish before checking whether any
                                // in-state site is in view.
                                this.map.once('moveend', () => {
                                    if (this.selected_state === state_abbr) this.reveal_nearest();
                                });
                                this.get_site_listing()?.unpin();
                                this.update_search_term();
                                const filters_inner_el = document.querySelector('.pcsl-filters-inner');
                                filters_inner_el.classList.remove('open');
                                this._unalign_filters_group(filters_inner_el);

                                log_event('zoom_to_target', { target: line1 + ' ' + line2, state: state_abbr });
                            });
                            this.suggestions_list.appendChild(li);
                        });
                        this._sync_suggestions_class();
                    });
            }, 300);
        });

        this.zip_input.addEventListener('keydown', e => {
            if (e.key === 'Enter') {
                const first = this.suggestions_list.querySelector('li:not(.pcsl-suggestion-no-results)');
                if (first) first.click();
            }
        });

        document.addEventListener('click', e => {
            if (!e.target.closest('#pcsl-zip-filter')) {
                this.suggestions_list.innerHTML = '';
                this._sync_suggestions_class();
            }
        });

        document.getElementById('pcsl-pin-code-select').addEventListener('change', () => {
            this.apply();
            this.update_search_term();
            this._update_type_badge();
            const filters_inner_select = document.querySelector('.pcsl-filters-inner');
            filters_inner_select.classList.remove('open');
            this._unalign_filters_group(filters_inner_select);
            log_event('filter_type', { type: document.getElementById('pcsl-pin-code-select').value });
        });
    }
}
